import { Button, Card, Empty, Input, Select, Space, Table, Tag } from 'antd';
import { PlusOutlined } from '@ant-design/icons';
import { useQuery } from '@tanstack/react-query';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import api from '@/services/api.service';
import { useAuthStore } from '@/store/authStore';
import EducationPageChrome from '@/pages/education/components/EducationPageChrome';

type CertificateRow = {
  id: string;
  tc_number?: string | null;
  status: string;
  student_name?: string | null;
  admission_no?: string | null;
  last_grade?: string | null;
  issued_on?: string | null;
  exit_on?: string | null;
  created_at?: string | null;
};

const STATUSES = ['draft', 'issued'] as const;

function statusColor(status?: string): string | undefined {
  if (status === 'issued') return 'green';
  return 'default';
}

export default function TransferCertificateListPage() {
  const { t } = useTranslation('education');
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const canIssue = Boolean(
    useAuthStore((state) => state.user?.permissions?.includes('education.transfer_certificate.issue'))
  );
  const status = params.get('status') || undefined;
  const q = params.get('q') || undefined;

  const query = useQuery({
    queryKey: ['education-transfer-certificates', status, q],
    queryFn: () =>
      api.get<CertificateRow[]>('/education/transfer-certificates', {
        params: { status, q },
      }),
  });
  const rows = Array.isArray(query.data?.data) ? query.data!.data : [];

  const setFilter = (key: string, value?: string) => {
    const next = new URLSearchParams(params);
    if (value) next.set(key, value);
    else next.delete(key);
    setParams(next);
  };

  return (
    <EducationPageChrome
      title={t('transferCertificate')}
      description={t('transferCertificateIntro')}
      tags={
        <Tag>
          {rows.length} {t('certificates', { defaultValue: 'certificates' })}
        </Tag>
      }
      extra={
        canIssue ? (
          <Button
            type="primary"
            icon={<PlusOutlined />}
            onClick={() => navigate('/education/transfer-certificates/new')}
          >
            {t('newTransferCertificate', { defaultValue: 'New transfer certificate' })}
          </Button>
        ) : null
      }
    >
      <Card size="small">
        <Space direction="vertical" size="middle" style={{ width: '100%' }}>
          <Space wrap>
            <Input.Search
              allowClear
              placeholder={t('search')}
              defaultValue={q}
              onSearch={(value) => setFilter('q', value || undefined)}
              style={{ width: 240 }}
            />
            <Select
              allowClear
              placeholder={t('status')}
              value={status}
              style={{ minWidth: 160 }}
              options={STATUSES.map((item) => ({ value: item, label: item }))}
              onChange={(value) => setFilter('status', value)}
            />
          </Space>
          <Table
            rowKey="id"
            loading={query.isLoading}
            dataSource={rows}
            pagination={{ pageSize: 20, showSizeChanger: true }}
            onRow={(row) => ({
              onClick: () => navigate(`/education/transfer-certificates/${row.id}`),
              style: { cursor: 'pointer' },
            })}
            locale={{
              emptyText: (
                <Empty
                  image={Empty.PRESENTED_IMAGE_SIMPLE}
                  description={t('transferCertificateEmpty', {
                    defaultValue: 'No transfer certificates yet. Draft one from an enrollment, then issue it.',
                  })}
                />
              ),
            }}
            columns={[
              {
                title: t('name'),
                dataIndex: 'student_name',
                render: (value?: string | null) => value || '—',
              },
              {
                title: t('admissionNo'),
                dataIndex: 'admission_no',
                width: 130,
                render: (value?: string | null) => value || '—',
              },
              {
                title: t('lastGrade'),
                dataIndex: 'last_grade',
                width: 120,
                render: (value?: string | null) => value || '—',
              },
              {
                title: t('transferCertificate'),
                dataIndex: 'tc_number',
                width: 150,
                render: (value?: string | null) => value || t('notIssued'),
              },
              {
                title: t('status'),
                dataIndex: 'status',
                width: 110,
                render: (value: string) => <Tag color={statusColor(value)}>{value}</Tag>,
              },
              {
                title: t('exitOn'),
                dataIndex: 'exit_on',
                width: 120,
                render: (value?: string | null) => (value ? String(value).slice(0, 10) : '—'),
              },
              {
                title: t('createdAt', { defaultValue: 'Created' }),
                dataIndex: 'created_at',
                width: 120,
                render: (value?: string | null) => (value ? String(value).slice(0, 10) : '—'),
              },
            ]}
          />
        </Space>
      </Card>
    </EducationPageChrome>
  );
}
